import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { verifyAge, AgeVerificationResult } from '../utils/ageVerification';
import ParentalConsent from './ParentalConsent';
import { ParentalConsentData } from '../utils/ageVerification';

const Signup: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    role: 'student' as 'student' | 'teacher',
    birthdate: '' // Add birthdate for age verification
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCleanupOption, setShowCleanupOption] = useState(false);
  const [ageVerification, setAgeVerification] = useState<AgeVerificationResult | null>(null);
  const [showParentalConsent, setShowParentalConsent] = useState(false);
  const [parentalConsentData, setParentalConsentData] = useState<ParentalConsentData | null>(null);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Check age verification when birthdate changes
    if (name === 'birthdate' && value && formData.role === 'student') {
      const verification = verifyAge(value);
      setAgeVerification(verification);
      
      if (verification.requiresParentalConsent) {
        setShowParentalConsent(true);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }
    
    if (formData.password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    // Age verification for students
    if (formData.role === 'student') {
      if (!formData.birthdate) {
        return setError('Birth date is required for students');
      }

      const verification = verifyAge(formData.birthdate);
      if (!verification.canProceed) {
        return setError(verification.message || 'Age verification failed');
      }

      // Check if parental consent is required and provided
      if (verification.requiresParentalConsent && !parentalConsentData) {
        return setError('Parental consent is required for users under 13');
      }
    }
    
    try {
      setError('');
      setLoading(true);
      
      const userData = {
        role: formData.role,
        name: '', // Will be filled in profile setup
        nickname: '', // Will be filled in profile setup
        instrument: '', // Will be filled in profile setup
        birthdate: formData.role === 'student' ? formData.birthdate : '', // Include birthdate for students
        weekStartDay: 'Sunday' as const,
        hasCompletedSetup: false, // Profile setup not completed yet
        emailVerified: false, // Will be updated after email verification
        // Add parental consent data for under-13 users
        ...(formData.role === 'student' && ageVerification?.requiresParentalConsent && parentalConsentData ? {
          parentalConsent: parentalConsentData
        } : {})
      };
      
      await signup(formData.email, formData.password, userData);
      navigate('/email-verification');
    } catch (error: any) {
      console.error('Signup error:', error);
      
      // Check if the error is due to email already being in use
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. If you created an account but didn\'t verify your email, we can help you start fresh. If you already have a verified account, please try signing in instead.');
        setShowCleanupOption(true);
      } else {
        setError('Failed to create an account');
      }
    }
    
    setLoading(false);
  };

  const handleParentalConsentGiven = (consentData: ParentalConsentData) => {
    setParentalConsentData(consentData);
    setShowParentalConsent(false);
    setError(''); // Clear any previous errors
  };

  const handleParentalConsentDenied = () => {
    setShowParentalConsent(false);
    setError('Parental consent is required for users under 13. Account creation cannot proceed.');
  };

  const handleCleanupUnverifiedAccount = async () => {
    try {
      setLoading(true);
      setError('');
      
      const cleanupUnverifiedAccount = httpsCallable(functions, 'cleanupUnverifiedAccount');
      const result = await cleanupUnverifiedAccount({ email: formData.email });
      
      if ((result.data as any).success) {
        setError('');
        setShowCleanupOption(false);
        // Now try to sign up again
        await handleSubmit({ preventDefault: () => {} } as React.FormEvent);
      } else {
        setError('Failed to clean up account. Please contact support.');
      }
    } catch (error: any) {
      console.error('Cleanup error:', error);
      if (error.code === 'functions/permission-denied') {
        setError('This account is already verified! Please try signing in instead. If you can\'t access your account, try the "Forgot Password" option on the login page.');
      } else {
        setError('Failed to clean up account. Please contact support.');
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to="/login"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              sign in to existing account
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Type
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="role"
                    value="student"
                    checked={formData.role === 'student'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  Student
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="role"
                    value="teacher"
                    checked={formData.role === 'teacher'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  Teacher
                </label>
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Password (min 6 characters)"
                value={formData.password}
                onChange={handleChange}
              />
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Confirm password"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </div>

            {/* Birth Date - Only for students */}
            {formData.role === 'student' && (
              <div>
                <label htmlFor="birthdate" className="block text-sm font-medium text-gray-700">
                  Birth Date (YYYY-MM-DD) *
                </label>
                <input
                  id="birthdate"
                  name="birthdate"
                  type="date"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  value={formData.birthdate}
                  onChange={handleChange}
                />
                {ageVerification && (
                  <div className={`mt-2 text-sm ${
                    ageVerification.requiresParentalConsent ? 'text-yellow-600' : 
                    ageVerification.canProceed ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {ageVerification.message}
                    {ageVerification.requiresParentalConsent && (
                      <div className="mt-1 text-xs">
                        Parental consent will be required to complete registration.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}

          {showCleanupOption && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="text-yellow-800 text-sm mb-3">
                <strong>Account exists but needs verification!</strong><br/>
                If you created an account before but didn't verify your email, we can help you start fresh. 
                If your account is already verified, please try{' '}
                <Link to="/login" className="font-medium text-yellow-900 underline">
                  signing in instead
                </Link>.
              </div>
              <button
                type="button"
                onClick={handleCleanupUnverifiedAccount}
                disabled={loading}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
              >
                {loading ? 'Cleaning up...' : 'Remove old account and start fresh'}
              </button>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </div>
        </form>
      </div>

      {/* Parental Consent Modal */}
      {showParentalConsent && ageVerification && (
        <ParentalConsent
          onConsentGiven={handleParentalConsentGiven}
          onConsentDenied={handleParentalConsentDenied}
          childName={formData.email} // Use email as placeholder for child name
          childAge={ageVerification.age}
        />
      )}

      {/* Footer */}
      <div className="mt-8 text-center">
        <div className="flex justify-center space-x-6 text-sm text-gray-500">
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-indigo-600 transition-colors"
          >
            Privacy Policy
          </a>
          <a
            href="/terms-of-use"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-indigo-600 transition-colors"
          >
            Terms of Use
          </a>
        </div>
        <p className="mt-2 text-xs text-gray-400">
          By signing up, you agree to our Terms of Use and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default Signup;
